import { useMutation, useQueryClient } from '@tanstack/react-query';
import BirthService, {
  IUpdateBirthParams,
} from '@clickersphere/core/services/BirthService';
import { useBuildQueryKey } from '../useBuildQueryKey';
import { BIRTHS_QUERY_KEY, BirthQueryData } from './useBirths';

export function useUpdateBirth() {
  const queryClient = useQueryClient();
  const { buildQueryKey } = useBuildQueryKey();

  const { isPending: updateBirthIsPending, mutateAsync: updateBirthAsync } =
    useMutation({
      mutationFn: async ({
        id,
        data,
      }: {
        id: string;
        data: IUpdateBirthParams;
      }) => BirthService.update(id, { ...data }),

      onMutate: async (variables) => {
        await queryClient.cancelQueries({
          queryKey: buildQueryKey(BIRTHS_QUERY_KEY),
        });

        const oldBirthsQueries = queryClient.getQueriesData<BirthQueryData[]>({
          queryKey: buildQueryKey(BIRTHS_QUERY_KEY),
        });

        oldBirthsQueries.forEach(([queryKey]) => {
          queryClient.setQueryData<BirthQueryData[]>(queryKey, (old) =>
            old?.map((birth) =>
              birth.id === variables.id
                ? { ...birth, ...variables.data, queryStatus: 'pending' }
                : birth,
            ),
          );
        });

        return { oldBirthsQueries };
      },

      onSuccess: async (_data, variables) => {
        await queryClient.cancelQueries({
          queryKey: buildQueryKey(BIRTHS_QUERY_KEY),
        });

        queryClient
          .getQueriesData<BirthQueryData[]>({
            queryKey: buildQueryKey(BIRTHS_QUERY_KEY),
          })
          .forEach(([queryKey]) => {
            queryClient.setQueryData<BirthQueryData[]>(queryKey, (old) =>
              old?.map((birth) =>
                birth.id === variables.id
                  ? { ...birth, queryStatus: undefined }
                  : birth,
              ),
            );
          });
      },

      onError: async (_error, _variables, context) => {
        await queryClient.cancelQueries({
          queryKey: buildQueryKey(BIRTHS_QUERY_KEY),
        });

        context?.oldBirthsQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      },
    });

  return {
    updateBirthAsync,
    updateBirthIsPending,
  };
}
